import { EMAIL_DOC_ALIGNMENTS, emailDocPageStyleRejections } from '@idc-forms/formula/email-doc'
import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { NOTIFICATION_RECIPIENT } from '../entities/state/state'
import { MoneySchema } from '../primitives/money'
import { InstantSchema, LocalDateSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de CRÉDITOS en API v2.
 *
 * Entre el cierre de la venta (`PROMESA FIRMADA`) y la entrega del inmueble hay 4 a 6 meses de
 * trámite bancario que hoy viven fuera del sistema. Créditos es ese tramo: un proceso de 7 etapas
 * con checklist, operado por administración y leído por todos.
 *
 * ── LAS TRES ENTIDADES, Y POR QUÉ SON TRES ───────────────────────────────────
 *
 * | | |
 * |---|---|
 * | `ctp_` **plantilla** | La identidad estable —«Proceso de crédito IDC»—. No tiene etapas propias |
 * | `ctv_` **versión** | El contenido: etapas, ítems, campos, bifurcación, disparo y perillas |
 * | `crd_` **expediente** | El trámite de UNA negociación, con la versión ya MATERIALIZADA adentro |
 *
 * La materialización es la decisión que obliga a separar plantilla de versión: **publicar una
 * versión nueva NO toca los expedientes en curso**. Es la misma decisión que
 * `PortalFormRequest.enabledSections`, y por el mismo motivo — una corrida dura meses, y cambiarle
 * las reglas a mitad de camino a alguien que ya contestó media checklist no es una mejora, es
 * perderle el trabajo.
 *
 * ── LO QUE NO ES ─────────────────────────────────────────────────────────────
 *
 * **El proceso no bloquea.** Ni avanzar de etapa (R3: «no tiene que bloquear, pero sí avisarnos»)
 * ni los adjuntos faltantes (R5). Por eso `requirement` tiene TRES estados y no un booleano: el
 * default del negocio es avisar y dejar pasar, y `blocking` es la excepción que hay que declarar.
 *
 * Los avisos al cliente sólo existen cuando una plantilla publicada los declara. La edición y la
 * publicación están protegidas por `ManageCreditTemplates`, y el interruptor
 * `FEATURE_CREDIT_CLIENT_NOTICES` permite detener esa audiencia sin alterar la versión.
 *
 * **Créditos no escribe a SAP** (R9). No tiene efecto transaccional.
 *
 * ── LA REGLA QUE ATRAVIESA TODO EL ARCHIVO ───────────────────────────────────
 *
 * ☠️ **Ningún valor viaja como `Mixed`, y ninguna unión discrimina por booleano.**
 *
 * Lo primero es lo que hace consultable el reporte de tiempos (R7): se mide por etapa Y por evento
 * dentro de la etapa, así que `enteredAt`/`exitedAt` y `answeredAt`/`answeredBy` tienen que ser
 * campos indexables, no un blob. Un `Mixed` responde «¿cuánto tardó Avalúos?» sólo con un barrido
 * en memoria.
 *
 * Lo segundo es mecánico y muerde en compilación: la API corre con `strictNullChecks: false`, y sin
 * él TypeScript **no angosta** una unión por un literal booleano —después de `if (!x.ok)` sigue
 * viendo la unión entera—. Con un discriminante STRING angosta bien. Referencia viva:
 * `apps/api-real-estate/src/v2/reservations/domain/reservation-window.ts`.
 */

/**
 * Mensajes de validación, en español porque los lee quien llena el formulario.
 *
 * Va ARRIBA de todo y no junto a las entradas: los schemas se construyen al importar el módulo, y
 * una `const` referenciada antes de su declaración muere con `ReferenceError` en el import — no en
 * la llamada, donde se buscaría.
 */
const MESSAGES = {
  NEGOTIATION_REQUIRED: 'Selecciona la negociación del trámite.',
  FINANCING_TYPE_REQUIRED: 'Elige si el cliente paga con crédito bancario o de contado.',
  RECIPIENT_EMAIL: 'Escribe un correo electrónico válido.',
  SELECT_OPTIONS: 'Agrega al menos una opción para elegir.',
  NOTE_MAX: 'La nota no puede superar los 2000 caracteres.',
  REASON_MAX: 'El motivo no puede superar los 500 caracteres.'
} as const

/* ────────────────────────────────────────────────────────────────────────────
 * CATÁLOGOS
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Estado del EXPEDIENTE.
 *
 * `approved` es el final feliz: la negociación queda «lista para entrega». No incluye
 * `not_started` —un expediente que no existe no tiene estado—; eso es un estado de la FILA del
 * listado y vive en `CREDIT_ROW_STATUS`.
 */
export const CREDIT_STATUS = {
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved',
  CANCELLED: 'cancelled'
} as const
export type CreditStatus = (typeof CREDIT_STATUS)[keyof typeof CREDIT_STATUS]
export const CreditStatusSchema = z.enum([
  CREDIT_STATUS.IN_PROGRESS,
  CREDIT_STATUS.APPROVED,
  CREDIT_STATUS.CANCELLED
])

/**
 * Cómo paga el cliente. Es la única bifurcación del proceso.
 *
 * Se elige DENTRO de Créditos aunque la negociación tenga un campo parecido (R8): la negociación
 * cerrada no se toca, y de las 163 cerradas **129 (79%) llegan sin declararla** —medido contra
 * producción el 2026-08-21—. Por eso el selector de modalidad es fuente primaria y va prominente
 * en la pantalla, no un dato heredado que se asume.
 *
 * `null` es un estado legítimo y frecuente: el expediente arrancó y todavía nadie eligió.
 */
export const FINANCING_TYPE = {
  CREDIT: 'credit',
  CASH: 'cash'
} as const
export type FinancingType = (typeof FINANCING_TYPE)[keyof typeof FINANCING_TYPE]
export const FinancingTypeSchema = z.enum([FINANCING_TYPE.CREDIT, FINANCING_TYPE.CASH])

/**
 * Estado de una etapa dentro del expediente.
 *
 * `skipped` NO es «sin datos»: es el resultado declarado de la bifurcación. Contado saltea
 * Integración de expediente y Avalúos, y esas dos etapas quedan `skipped` —no `pending` para
 * siempre, que es como se vería un proceso trabado.
 */
export const STAGE_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  SKIPPED: 'skipped'
} as const
export type StageStatus = (typeof STAGE_STATUS)[keyof typeof STAGE_STATUS]
export const StageStatusSchema = z.enum([
  STAGE_STATUS.PENDING,
  STAGE_STATUS.ACTIVE,
  STAGE_STATUS.COMPLETED,
  STAGE_STATUS.SKIPPED
])

/** Tipo de dato que captura un campo de un ítem. Gobierna el control que pinta la UI. */
export const FIELD_TYPE = {
  TEXT: 'text',
  LONGTEXT: 'longtext',
  NUMBER: 'number',
  MONEY: 'money',
  DATE: 'date',
  SELECT: 'select',
  CHECKBOX: 'checkbox',
  FILE: 'file'
} as const
export type FieldType = (typeof FIELD_TYPE)[keyof typeof FIELD_TYPE]
export const FieldTypeSchema = z.enum([
  FIELD_TYPE.TEXT,
  FIELD_TYPE.LONGTEXT,
  FIELD_TYPE.NUMBER,
  FIELD_TYPE.MONEY,
  FIELD_TYPE.DATE,
  FIELD_TYPE.SELECT,
  FIELD_TYPE.CHECKBOX,
  FIELD_TYPE.FILE
])

/**
 * Cuánto pesa que un campo esté vacío. **Es un control de TRES estados, no un booleano.**
 *
 * | | |
 * |---|---|
 * | `optional` | No se pide ni se menciona |
 * | `required` | **Avisa y deja pasar** — es el default del proceso (R3) |
 * | `blocking` | Frena el avance de la etapa. La excepción, y hay que declararla |
 *
 * Un booleano `required` no puede expresar la diferencia entre los dos últimos, y esa diferencia
 * ES el proceso: administración quiere enterarse de lo que falta, no quedarse trabada.
 */
export const FIELD_REQUIREMENT = {
  OPTIONAL: 'optional',
  REQUIRED: 'required',
  BLOCKING: 'blocking'
} as const
export type FieldRequirement = (typeof FIELD_REQUIREMENT)[keyof typeof FIELD_REQUIREMENT]
export const FieldRequirementSchema = z.enum([
  FIELD_REQUIREMENT.OPTIONAL,
  FIELD_REQUIREMENT.REQUIRED,
  FIELD_REQUIREMENT.BLOCKING
])

/**
 * A qué modalidad aplica un ítem.
 *
 * Los ítems de cumplimiento —carta de procedencia de fondos, documentos de cumplimiento— son
 * `cash`: no son trámite bancario, son prevención de lavado. Un ítem `cash` no se le muestra a un
 * expediente de crédito ni cuenta para su progreso.
 */
export const ITEM_APPLIES_TO = {
  BOTH: 'both',
  CREDIT: 'credit',
  CASH: 'cash'
} as const
export type ItemAppliesTo = (typeof ITEM_APPLIES_TO)[keyof typeof ITEM_APPLIES_TO]
export const ItemAppliesToSchema = z.enum([
  ITEM_APPLIES_TO.BOTH,
  ITEM_APPLIES_TO.CREDIT,
  ITEM_APPLIES_TO.CASH
])

/**
 * Qué dispara marcar un ítem. **Los ítems avisan, no las etapas** (R2).
 *
 * `notify_client` existe porque la fuente pide avisos al comprador —Excel E1 y transcripción
 * l.171-181—. Sus destinatarios forman parte de la plantilla publicada; el interruptor operativo
 * puede excluirlos sin quitar los avisos internos.
 */
export const EFFECT_KIND = {
  NONE: 'none',
  NOTIFY_STAFF: 'notify_staff',
  NOTIFY_CLIENT: 'notify_client'
} as const
export type EffectKind = (typeof EFFECT_KIND)[keyof typeof EFFECT_KIND]

/**
 * A QUIÉN se le avisa. Lo declara el binding del dominio, no el kernel.
 *
 * `role` y `fixed` llevan carga —cuál rol, cuáles correos—, así que el destinatario es una unión
 * discriminada y no una lista de strings sueltos: un `'role'` sin el rol es un aviso que no se
 * puede mandar y que nadie descubre hasta que no llega.
 */
export const RECIPIENT_KIND = {
  /** El asesor de la negociación. */
  ADVISOR: 'advisor',
  /** Quien abrió el expediente. */
  CREATOR: 'creator',
  /** Todos los usuarios de un rol. */
  ROLE: 'role',
  /** Correos escritos a mano en la plantilla. */
  FIXED: 'fixed',
  /** Alias legacy del propietario principal. Se acepta, pero el editor ya no lo escribe. */
  CLIENT: 'client',
  /** El propietario principal de la negociación. */
  PRINCIPAL_OWNER: 'principalOwner',
  /** Todos los propietarios de la negociación. */
  ALL_OWNERS: 'allOwners',
  /** Una persona concreta, identificada por `usr_`. */
  USER: 'user',
  /** Un correo resuelto desde una variable `email` disponible para el aviso. */
  VARIABLE: 'variable'
} as const
export type RecipientKind = (typeof RECIPIENT_KIND)[keyof typeof RECIPIENT_KIND]

export const CreditRecipientSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(RECIPIENT_KIND.ADVISOR) }),
  z.object({ kind: z.literal(RECIPIENT_KIND.CREATOR) }),
  z.object({ kind: z.literal(RECIPIENT_KIND.ROLE), role: PublicIdSchemas.role }),
  z.object({
    kind: z.literal(RECIPIENT_KIND.FIXED),
    emails: z.array(z.string().trim().email(MESSAGES.RECIPIENT_EMAIL)).min(1)
  }),
  z.object({ kind: z.literal(RECIPIENT_KIND.CLIENT) }),
  z.object({ kind: z.literal(RECIPIENT_KIND.PRINCIPAL_OWNER) }),
  z.object({ kind: z.literal(RECIPIENT_KIND.ALL_OWNERS) }),
  z.object({
    kind: z.literal(RECIPIENT_KIND.USER),
    user: PublicIdSchemas.user,
    label: z.string().trim().max(200).optional()
  }),
  z.object({
    kind: z.literal(RECIPIENT_KIND.VARIABLE),
    variable: z
      .string()
      .trim()
      .min(1)
      .max(160)
      .regex(/^[a-zA-Z0-9._-]+$/),
    label: z.string().trim().max(200).optional()
  })
])

const CreditRecipientListSchema = z.array(CreditRecipientSchema).max(100)

/* ────────────────────────────────────────────────────────────────────────────
 * REFERENCIAS
 * ──────────────────────────────────────────────────────────────────────────── */

/** Autoría: `usr_`, nunca el `_id` ni el documento del usuario. */
export const CreditUserRefSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string(),
    /** Foto de perfil; `null` cae a la inicial. */
    avatar: z.string().nullable()
  })
  .nullable()

/**
 * Referencia a un catálogo.
 *
 * `code` es STRING: los códigos de inmueble y proyecto son alfanuméricos (`CNAST2502`). El de la
 * NEGOCIACIÓN es un contador y viaja `number` — declarar cualquiera de los dos al revés ya hizo
 * una vez que viajaran todos `null` en el listado hermano de Postventa.
 */
const namedRef = <T extends z.ZodTypeAny>(id: T) =>
  z.object({ id, name: z.string(), code: z.string().nullable() }).nullable()

/**
 * Un archivo colgado de un campo `file`.
 *
 * Viaja resuelto —y no como un `att_` pelado— porque el expediente se lee entero de una: pintar la
 * checklist con nombres de archivo no puede costar una llamada por adjunto. Molde:
 * `V2UploadedDocumentSchema` de `admin-documents.ts`.
 */
export const CreditAttachmentRefSchema = z.object({
  id: PublicIdSchemas.attachment,
  name: z.string(),
  size: z.number().int().nonnegative(),
  mimeType: z.string().nullable()
})

/* ────────────────────────────────────────────────────────────────────────────
 * CAMPOS: configuración y valor
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Cómo se pinta y se valida un campo, según su tipo.
 *
 * `kind` **repite el `type` del campo** en vez de derivarse de él: así el config se angosta solo y
 * es imposible guardar la configuración de un `select` en un campo `date`.
 *
 * Deliberadamente MÍNIMO: sólo lo que hace falta para renderizar el control (las opciones de un
 * select, la moneda de un monto, los límites de un archivo). Los refinamientos del constructor de
 * formularios —largos máximos, rangos— los agrega SPEC-10 cuando la pantalla los pida; sumar
 * campos opcionales después no rompe a nadie, sacarlos sí.
 */
export const CreditFieldConfigSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(FIELD_TYPE.TEXT),
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional()
  }),
  z.object({
    kind: z.literal(FIELD_TYPE.LONGTEXT),
    maxLength: z.number().int().positive().optional()
  }),
  z.object({
    kind: z.literal(FIELD_TYPE.NUMBER),
    min: z.number().optional(),
    max: z.number().optional()
  }),
  z.object({
    kind: z.literal(FIELD_TYPE.MONEY),
    /** Moneda con la que se propone el campo. El valor guardado manda sobre ésta. */
    currency: MoneySchema.shape.currency
  }),
  z.object({
    kind: z.literal(FIELD_TYPE.DATE),
    min: LocalDateSchema.optional(),
    max: LocalDateSchema.optional()
  }),
  z.object({
    kind: z.literal(FIELD_TYPE.SELECT),
    /**
     * `value` es la JOIN KEY y es estable; `label` es lo que se muestra y se puede corregir.
     * Separarlos es lo que permite arreglar una etiqueta mal escrita sin invalidar lo ya
     * respondido.
     */
    options: z
      .array(z.object({ value: z.string().min(1), label: z.string().min(1) }))
      .min(1, MESSAGES.SELECT_OPTIONS),
    /** Un `select` múltiple guarda varias opciones; el simple, exactamente una. */
    multiple: z.boolean()
  }),
  z.object({ kind: z.literal(FIELD_TYPE.CHECKBOX) }),
  z.object({
    kind: z.literal(FIELD_TYPE.FILE),
    /** Lista vacía = cualquier tipo. La UI la usa para el `accept` del input. */
    allowedMimeTypes: z.array(z.string()),
    maxSizeBytes: z.number().int().positive(),
    minFiles: z.number().int().nonnegative(),
    maxFiles: z.number().int().positive()
  })
])

/**
 * El VALOR respondido. ☠️ Nunca `Mixed`.
 *
 * Son ocho formas para ocho tipos, pero **no mapean uno a uno** y eso es a propósito: `text` y
 * `longtext` guardan lo mismo (`text`), `select` guarda `option`, `checkbox` guarda `bool` y
 * `file` guarda `files`. Lo que cambia entre `text` y `longtext` es el control que se pinta, no el
 * dato — darles dos formas distintas obligaría a migrar datos el día que alguien agrande un campo.
 *
 * El monto usa el primitivo `MoneySchema` (`amountMinor` entero + moneda) y no un número suelto:
 * es la diferencia entre guardar dinero y guardar un `float` que redondea distinto según quién lo
 * sume.
 */
export const CreditFieldValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string() }),
  z.object({ kind: z.literal('number'), number: z.number() }),
  MoneySchema.extend({ kind: z.literal('money') }),
  z.object({ kind: z.literal('date'), date: LocalDateSchema }),
  z.object({ kind: z.literal('bool'), bool: z.boolean() }),
  /** Los `value` elegidos. El `label` sale de la config MATERIALIZADA en el mismo expediente. */
  z.object({ kind: z.literal('option'), options: z.array(z.string()) }),
  z.object({ kind: z.literal('files'), attachments: z.array(CreditAttachmentRefSchema) })
])

/* ────────────────────────────────────────────────────────────────────────────
 * LA PLANTILLA: `ctp_` y sus versiones `ctv_`
 * ──────────────────────────────────────────────────────────────────────────── */

/** Un campo TAL COMO LO DECLARA la plantilla — sin valor: todavía no hay expediente. */
export const CreditTemplateFieldSchema = z.object({
  /** JOIN KEY estable. **Jamás se deriva del label**: renombrar no puede huerfanar respuestas. */
  key: z.string().min(1),
  order: z.number().int().nonnegative(),
  label: z.string().min(1),
  description: z.string().nullable(),
  placeholder: z.string().nullable(),
  type: FieldTypeSchema,
  requirement: FieldRequirementSchema,
  config: CreditFieldConfigSchema,
  /**
   * Cuándo se muestra el campo. `null` = siempre.
   *
   * La cadena lleva un `ConditionExpr` serializado (`@idc-forms/formula/show-when`): una o más
   * cláusulas —variable + operador + valor, o una fórmula booleana— unidas por Y/O. El tipo de la
   * conexión no cambió al pasar del token pelado a la condición compuesta, así que las versiones
   * publicadas con un `{{ etapa4.banco }}` siguen siendo válidas y se leen como «ese token es
   * verdadero». `parseShowWhen` entiende las dos formas; el editor normaliza la vieja al abrir.
   */
  showWhen: z.string().min(1).nullable().default(null),
  /** El sistema lo completa; administración puede configurarlo pero no convertirlo en respuesta manual. */
  readOnly: z.boolean().default(false)
})

/*
 * ⛔ ACÁ VIVÍA `CREDIT_EMAIL_LAYOUT` (`internal` | `client`), Y MURIÓ. NO LO REPONGAS.
 *
 * Era el «Formato del correo»: un `select` de dos valores que elegía cuál de los TRES marcos
 * server-side dibujaba el aviso —logo de IDC contra portada de Condado Naranjo, con o sin enlace al
 * panel—. El marco entero era código: el encabezado, la franja, la ficha del expediente, el botón
 * «Ver expediente» y el pie no se podían tocar desde el editor.
 *
 * ── POR QUÉ NO ALCANZABA CON AGREGARLE UN TERCER VALOR ───────────────────────
 *
 * Porque la audiencia no es un enum: es un DISEÑO. Cada formato nuevo pedía una plantilla `.tsx`
 * nueva, un valor nuevo en este `enum`, una rama nueva en el adaptador y un despliegue — para algo
 * que es contenido. Y mientras tanto el editor mostraba un lienzo que no era el correo: se redactaba
 * el medio y los bordes los ponía otro.
 *
 * ── LO QUE OCUPA SU LUGAR ────────────────────────────────────────────────────
 *
 * El catálogo de PLANTILLAS del editor. «Aviso interno» trae el logo de IDC y la franja; «Aviso al
 * cliente», la marca de Condado Naranjo y el pie del portal. Son documentos: se eligen, se editan,
 * se les mueve una fila de la ficha y se les cambia el color del botón. El servidor quedó con UN
 * envoltorio genérico (`packages/email/src/emails/credits/credit-notice.tsx`) que no inyecta nada.
 *
 * ⚠️ El campo sigue existiendo en los documentos ya guardados en Mongo y no se lee: es expansión al
 * revés —contract— y el dato viejo no molesta a nadie. Lo que NO puede volver es el `select`.
 */

/**
 * El correo REDACTADO en el editor, tal como sale.
 *
 * ☠️ Hasta SPEC-14 el cuerpo se componía en el editor, se guardaba con el nodo… y no llegaba a
 * ningún lado: el envío usaba la plantilla interna y el texto quedaba de adorno (SPEC-10B, desvío
 * D1). Acá el mensaje pasa a ser parte del EFECTO, así que viaja igual que el resto: se materializa
 * con el expediente y lo lee el adaptador al mandar.
 *
 * `null` = el aviso no trae redacción y se manda con la plantilla de siempre (`template`). Es lo
 * que mantiene funcionando a los efectos que ya existían.
 *
 * El cuerpo lleva tokens `{{ … }}` de variables río arriba; **no es HTML** y no se interpreta como
 * tal: se resuelve contra el expediente y se renderiza como párrafos.
 */
/**
 * Los topes del árbol, medidos sobre los documentos reales del spike.
 *
 * ☠️ ESPEJO DELIBERADO de `EMAIL_DOC_LIMITS` en `@idc-forms/formula/email-doc`, y no un import:
 * `@idc-forms/schemas` no depende de `@idc-forms/formula` —lo consumen el SDK público y la app
 * móvil, que no tienen por qué arrastrar el evaluador de fórmulas— y sumarle esa dependencia por
 * dos números sería pagar un grafo entero por una constante. El guardarraíl contra la deriva es el
 * spec de `email-doc.spec.ts`, que afirma los mismos valores contra los mismos fixtures.
 *
 * `nodes` cuenta el árbol ENTERO porque en TipTap el texto son nodos hoja y el primer nivel es
 * siempre un solo `container`.
 *
 * ☠️ `depth` está en 16 porque el editor DEJA armar esa profundidad con bloques legítimos: sus tres
 * niveles de columnas anidadas (`MAX_COLUMNS_DEPTH`, un tope suyo) son SEIS niveles de árbol sin
 * una letra escrita, y la caja de nota del correo de apartado es una `section` dentro de otra. La
 * cadena completa —`container > section > section > cols > col > cols > col > cols > col > table >
 * tableRow > tableCell > paragraph > text`— da 14. Un tope por debajo de eso devuelve 422 sobre
 * documentos que no tienen nada malo; el que muerde de verdad es `nodes`.
 */
const EMAIL_DOC_TREE_LIMITS = { nodes: 3_000, depth: 16 } as const

/**
 * Cuántos nodos y cuántos niveles tiene el árbol.
 *
 * Camina ITERATIVO, con su propia pila: un `superRefine` corre sobre lo que mandó un cliente, y un
 * árbol torcido a propósito no puede tumbar el proceso por desbordar la pila de llamadas antes de
 * llegar al `if` que lo iba a rechazar. Se corta apenas pasa el tope de nodos: contar los 200.000
 * de un documento malicioso no aporta nada que el mensaje de error necesite.
 */
const measureEmailDocTree = (
  blocks: ReadonlyArray<Record<string, unknown>>
): { nodes: number; depth: number } => {
  const pending: Array<{ node: unknown; depth: number }> = blocks.map((node) => ({
    node,
    depth: 1
  }))
  let nodes = 0
  let depth = 0

  while (pending.length > 0) {
    const current = pending.pop()
    if (!current) break
    nodes += 1
    if (current.depth > depth) depth = current.depth
    if (nodes > EMAIL_DOC_TREE_LIMITS.nodes) break

    const content = (current.node as { content?: unknown } | null)?.content
    if (!Array.isArray(content)) continue
    for (const child of content) pending.push({ node: child, depth: current.depth + 1 })
  }

  return { nodes, depth }
}

/**
 * El cuerpo de un correo escrito en el EDITOR DE DOCUMENTO (`@react-email/editor`, TipTap v3).
 *
 * ── QUÉ ES `blocks`, EXACTAMENTE ─────────────────────────────────────────────
 *
 * `blocks` es `editor.getJSON().content`: los nodos de PRIMER NIVEL del documento de TipTap, sin
 * el envoltorio `doc` — **excluyendo el nodo `globalContent`**.
 *
 * ☠️ Y ESA EXCLUSIÓN ES LO IMPORTANTE. El editor persiste el TEMA ENTERO —los paneles de estilo
 * completos— dentro de un nodo `globalContent` del propio documento: el spike lo midió en 7 KB, el
 * **45 % del JSON** de cada correo. Guardarlo tiene dos costos y ningún beneficio: cada aviso
 * carga ese peso, y el tema queda CONGELADO en cada documento —si mañana cambia el color de marca,
 * los avisos ya guardados conservan el viejo salvo que alguien los migre uno por uno—. Acá el tema
 * lo pone el MARCO, server-side, igual que el logo y el pie: es de la marca, no del aviso. El
 * `superRefine` de abajo rechaza ese nodo para que no entre por descuido.
 *
 * ⚠️ En la práctica `blocks` tiene UN solo elemento, un nodo `container`: el editor registra un
 * plugin (`containerEnforcer`) que en cada transacción envuelve todo lo que no sea `globalContent`
 * dentro de un `container`. No es un nodo que el autor inserte, es estructural y forzado — por eso
 * el tope de tamaño cuenta NODOS TOTALES y no elementos del primer nivel: contar el primer nivel
 * de este formato sería contar siempre 1. Ver `EMAIL_DOC_NODE_TYPES` en
 * `@idc-forms/formula/email-doc`, que es el contrato de qué nodos se dibujan.
 *
 * ── POR QUÉ CONVIVE CON `body` Y NO LO REEMPLAZA ─────────────────────────────
 *
 * Contrato ADITIVO. Los avisos que ya están publicados llevan `body` en texto plano, y una versión
 * publicada NO se reescribe: se materializa tal cual en cada expediente en curso. Sacar `body`
 * dejaría a esos avisos saliendo con la frase genérica, en silencio. Con `content` presente manda
 * el árbol; sin él, el texto de siempre.
 *
 * ── LOS NODOS NO SE TIPAN CAMPO POR CAMPO, Y ES DELIBERADO ───────────────────
 *
 * Un nodo de TipTap es un árbol abierto (`type`, `attrs`, `content`, `marks`) cuya forma la define
 * la librería, no nosotros. Congelarla acá haría que subir la versión del editor rompiera el
 * guardado de plantillas. Lo que SÍ se acota es lo que puede hacer daño, y se acota donde
 * corresponde: el render del correo tiene una lista blanca de tipos de nodo y `safeUrl`; el
 * publish recorre el árbol y rechaza toda variable que no esté río arriba.
 *
 * ── ⛔ `schemaVersion: 2` NO ES DUAL-SUPPORT ──────────────────────────────────
 *
 * La 1 era BlockNote y la 2 es TipTap: son dos formatos que no se parecen y **nada los traduce**.
 * Un `content` con `schemaVersion: 1` que quede en Mongo NO se lee: el aviso cae a `body` en texto
 * plano, que es su degradación correcta. La decisión de recrear a mano las plantillas de staging
 * ya está tomada; este literal es lo que la hace explícita en vez de silenciosa.
 */
/**
 * El ESTILO DE PÁGINA del correo: lo que rodea al documento.
 *
 * ── ☠️ LOS NOMBRES VAN CRUZADOS ─────────────────────────────────────────────
 *
 * `page` es la superficie de ATRÁS (en el correo, el `<Body>`) y `body` es la HOJA (el
 * `<Container>`). Son las claves de la referencia y se quedan en inglés: son DATO, y renombrarlas
 * rompería todo `pageStyle` ya guardado. El panel las muestra en español —«Página» y «Cuerpo»—
 * porque eso sí es copy. La traducción a propiedades CSS vive en `emailDocPageStyle`, una vez.
 *
 * ── OBJETO EXPLÍCITO, Y ESA ES LA DIFERENCIA CON `globalContent` ─────────────
 *
 * ⛔ Esto NO es «CSS global». `globalContent` —el tema del editor— sigue excluido por lo de
 * siempre: 7 KB por aviso y la marca congelada en cada documento. Y un campo de texto libre que
 * termine dentro de un `<style>` es un vector de inyección, así que el panel tampoco lo ofrece.
 * Acá hay TRECE campos con nombre, cada uno con la forma que su propiedad CSS admite.
 *
 * ── ⭐ LA VALIDACIÓN NO ES UNA COPIA: ES LA MISMA FUNCIÓN QUE DIBUJA ─────────
 *
 * `emailDocPageStyleRejections` es la mitad inversa de `emailDocPageStyle`, y las dos salen de la
 * misma tabla de campos. Un valor que el correo no va a dibujar da 422 al guardar, en vez de
 * guardarse y desaparecer sin ruido cuando alguien abra la bandeja.
 *
 * ⚠️ Y sí, esto importa `@idc-forms/formula` desde `@idc-forms/schemas`. El docblock de
 * `EMAIL_DOC_TREE_LIMITS`, acá arriba, afirma lo contrario: quedó viejo. La dependencia YA existe
 * —`packages/schemas/package.json` la declara desde el ADR 0036, que mudó ahí el vocabulario de
 * variables— y `email-doc.ts` no importa nada, así que no arrastra el evaluador de fórmulas: es un
 * módulo suelto y tree-shakeable. Los topes del árbol siguen espejados porque cambiarlos es otro
 * trabajo, no porque no se pueda.
 */
export const CreditEmailPageStyleSchema = z.object({
  page: z
    .object({
      background: z.string().max(200).optional(),
      padding: z.string().max(200).optional()
    })
    .optional(),
  body: z
    .object({
      align: z.enum(EMAIL_DOC_ALIGNMENTS).optional(),
      textColor: z.string().max(200).optional(),
      background: z.string().max(200).optional(),
      width: z.string().max(200).optional(),
      height: z.string().max(200).optional(),
      padding: z.string().max(200).optional(),
      margin: z.string().max(200).optional(),
      borderRadius: z.string().max(200).optional(),
      borderWidth: z.string().max(200).optional(),
      borderColor: z.string().max(200).optional()
    })
    .optional()
})
export type CreditEmailPageStyle = z.infer<typeof CreditEmailPageStyleSchema>

export const CreditEmailContentSchema = z
  .object({
    /**
     * Versión del formato. Hoy **2** (documento de TipTap). La 1 era el AST de BlockNote y ya no
     * se lee — ver el bloque de arriba.
     */
    schemaVersion: z.literal(2),
    blocks: z.array(z.record(z.unknown())).max(200),
    /**
     * El estilo de PÁGINA, por correo. Ausente = el envoltorio de fábrica, que es como salían
     * todos los avisos antes de que este campo existiera.
     */
    pageStyle: CreditEmailPageStyleSchema.nullish()
  })
  .superRefine((value, ctx) => {
    for (const path of emailDocPageStyleRejections(value.pageStyle)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pageStyle', ...path],
        message:
          'Ese valor no se puede dibujar en un correo: revisá el color o la medida. Lo que el envoltorio no dibuja no se guarda, para que el correo no salga distinto de lo que se ve en el editor.'
      })
    }

    for (const [index, block] of value.blocks.entries()) {
      if (block.type !== 'globalContent') continue
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blocks', index],
        message:
          'El nodo `globalContent` no se guarda: es el tema del editor (7 KB, el 45 % del documento) y lo pone el marco del correo, no el aviso. Guardá `editor.getJSON().content` sin ese nodo.'
      })
    }

    const { nodes, depth } = measureEmailDocTree(value.blocks)
    if (nodes > EMAIL_DOC_TREE_LIMITS.nodes)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blocks'],
        message: `El documento tiene ${nodes} nodos y el tope es ${EMAIL_DOC_TREE_LIMITS.nodes}.`
      })
    if (depth > EMAIL_DOC_TREE_LIMITS.depth)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['blocks'],
        message: `El documento anida ${depth} niveles y el tope es ${EMAIL_DOC_TREE_LIMITS.depth}.`
      })
  })
export type CreditEmailContent = z.infer<typeof CreditEmailContentSchema>

export const CreditNoticeMessageSchema = z.object({
  subject: z.string().trim().min(1).max(240),
  previewText: z.string().trim().max(320).default(''),
  /** El cuerpo en TEXTO PLANO (legado). Se usa sólo cuando no hay `content`. */
  body: z.string().max(20_000).default(''),
  /** El cuerpo como AST del editor de bloques. Cuando viene, manda sobre `body`. */
  content: CreditEmailContentSchema.nullable().default(null)
})

/**
 * Qué pasa cuando alguien marca este ítem.
 *
 * Discrimina por STRING `kind` y no por un booleano `notifies`, por la regla de arriba: con
 * `strictNullChecks:false` el booleano no angosta y `effect.template` deja de existir para el
 * compilador justo donde hace falta.
 *
 * ── LO QUE SPEC-14 LE SUMA, Y POR QUÉ VA ACÁ ─────────────────────────────────
 *
 * `message` y `recipients` son OPCIONALES y nacen en `null`: un efecto escrito antes de este spec
 * significa exactamente lo mismo que significaba. Se cuelgan del efecto —y no de la etapa, ni del
 * nodo del canvas— porque es lo único que viaja entero hasta el executor: el mismo objeto que dice
 * QUÉ pasa dice ahora CÓMO se ve y A QUIÉN le llega.
 *
 * ☠️ Y es lo que hace posible el pedido del negocio: dos avisos de la MISMA etapa con audiencias y
 * formatos distintos —el interno al equipo y el de marca al cliente—. Con los destinatarios sólo a
 * nivel de etapa, el segundo aviso le habría llegado a la audiencia del primero.
 *
 * `recipients: null` = los de la etapa (`notification` / `emailsNotification`), que es el
 * comportamiento de siempre.
 */
export const CreditItemEffectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal(EFFECT_KIND.NONE) }),
  z.object({
    kind: z.literal(EFFECT_KIND.NOTIFY_STAFF),
    template: z.string().min(1),
    message: CreditNoticeMessageSchema.nullable().default(null),
    /** Lista única legacy. Cuando no hay listas ricas, se interpreta completa como `to`. */
    recipients: CreditRecipientListSchema.nullable().default(null),
    to: CreditRecipientListSchema.optional(),
    cc: CreditRecipientListSchema.optional(),
    bcc: CreditRecipientListSchema.optional(),
    replyTo: CreditRecipientListSchema.max(1).optional()
  }),
  z.object({
    kind: z.literal(EFFECT_KIND.NOTIFY_CLIENT),
    template: z.string().min(1),
    message: CreditNoticeMessageSchema.nullable().default(null),
    /** Lista única legacy. Cuando no hay listas ricas, se interpreta completa como `to`. */
    recipients: CreditRecipientListSchema.nullable().default(null),
    to: CreditRecipientListSchema.optional(),
    cc: CreditRecipientListSchema.optional(),
    bcc: CreditRecipientListSchema.optional(),
    replyTo: CreditRecipientListSchema.max(1).optional()
  })
])

/**
 * Un efecto CONDICIONAL: el correo que sale sólo si una variable del expediente vale tal cosa.
 *
 * ── LA ORDEN DEL NEGOCIO, TEXTUAL ────────────────────────────────────────────
 *
 * «Si la resolución está aprobada mando este correo; rechazada, este otro; en revisión, este otro»
 * (SPEC-14). En el editor es un subgrafo —`Ítem → Condición(variable) → Aviso por valor`— y acá es
 * su forma compilada: una lista de reglas que el runtime evalúa al marcar el ítem.
 *
 * ── IGUALDAD, Y NADA MÁS ─────────────────────────────────────────────────────
 *
 * El predicado es `variable == equals` y punto: es lo único que el editor sabe producir (las
 * salidas de una Condición son valores de un dominio cerrado) y lo único que el usuario sabe leer
 * —⛔ nada técnico frente al usuario, `00-contexto.md` §5—. Ampliarlo a operadores es aditivo, y el
 * runtime ya lo evalúa con `evaluateGuard`, el evaluador PROMOVIDO del portal, para que el día que
 * se amplíe no haya un segundo evaluador que mantener.
 *
 * ── LAS REGLAS SUMAN, NO REEMPLAZAN ──────────────────────────────────────────
 *
 * Un ítem con reglas conserva su `effect` plano y lo sigue disparando: en el flujo real el equipo
 * quiere su aviso interno SIEMPRE y el cliente recibe además el que corresponde a la resolución.
 * Compatibilidad total con lo que ya estaba publicado.
 *
 * `equals` es JOIN KEY (el `value` de la opción), jamás la etiqueta visible.
 */
export const CreditEffectRuleSchema = z.object({
  /** Token de la variable, SIN llaves: `etapa4.resolutionResult`. */
  variable: z.string().trim().min(1).max(160),
  /** El valor que dispara la regla. Es la clave de la opción, no su etiqueta. */
  equals: z.string().trim().min(1).max(160),
  /** Lo que se lee en el correo y en la bitácora donde un ítem pondría su etiqueta. */
  label: z.string().trim().min(1).max(160),
  effect: CreditItemEffectSchema
})

export const CreditTemplateItemSchema = z.object({
  key: z.string().min(1),
  order: z.number().int().nonnegative(),
  label: z.string().min(1),
  hint: z.string().nullable(),
  appliesTo: ItemAppliesToSchema,
  /**
   * Se marca solo al activarse la etapa.
   *
   * Existe para el aviso de inicio: el ítem «se avisó al cliente que arranca el trámite» no es
   * trabajo de nadie, es la consecuencia de que la etapa abriera. Pedirle a administración que
   * tilde a mano algo que ya pasó es ruido.
   */
  autoCheckOnStageEnter: z.boolean(),
  /**
   * ⚠️ ESPEJO LEGADO del PRIMER aviso del punto. Se sigue leyendo y ya no es la fuente.
   *
   * Era la única ranura, y por eso el publish rechazaba un segundo Aviso anclado al mismo punto
   * (`DUPLICATE_ACTION_ITEM`): no tenía dónde escribirlo. El negocio pide lo contrario —el mismo
   * disparo con dos audiencias, el interno al equipo y el de marca al cliente— así que la ranura
   * pasó a lista (`effects`) y ésta queda como el espejo del primero, igual que `branch.routes` lo
   * es de `branch.routeList`.
   *
   * Precedencia, y es la misma que la de la bifurcación: **si `effects` trae algo, manda ella**.
   * `effect` se lee sólo cuando la lista está vacía, que es el caso de todo lo publicado hasta acá.
   */
  effect: CreditItemEffectSchema,
  /**
   * TODOS los avisos que dispara marcar este punto, en el orden en que se publicaron.
   *
   * Aditivo y con default: un punto publicado antes de esto no lo trae y leerlo no puede fallar. El
   * tope acompaña al de los avisos de una etapa — un punto que manda veinte correos no es una
   * plantilla, es un accidente.
   */
  effects: z.array(CreditItemEffectSchema).max(20).default([]),
  /**
   * Los avisos CONDICIONALES del ítem: «según cómo haya salido la resolución, este correo».
   *
   * Aditivo y con default: un ítem publicado antes de SPEC-14 no lo trae y leerlo no puede fallar.
   * Se evalúan al marcar el ítem, DESPUÉS del `effect` plano y sin reemplazarlo.
   */
  effectRules: z.array(CreditEffectRuleSchema).max(20).default([]),
  fields: z.array(CreditTemplateFieldSchema)
})

/**
 * Destinatario materializado en una etapa.
 *
 * Los cinco valores legacy siguen viajando sin cambios. `role:<rol_…>` es la única extensión:
 * conserva la referencia dinámica que eligió el nodo Acción sin congelar los correos del rol al
 * publicar. El runtime del kernel no interpreta este string; el binding de Créditos lo resuelve.
 */
export const CreditStageNotificationSchema = z.union([
  z.enum([
    NOTIFICATION_RECIPIENT.AUTHOR,
    NOTIFICATION_RECIPIENT.ADVISOR,
    NOTIFICATION_RECIPIENT.PRINCIPAL_OWNER,
    NOTIFICATION_RECIPIENT.ALL_OWNERS,
    NOTIFICATION_RECIPIENT.CUSTOM
  ]),
  z
    .string()
    .refine(
      (value) =>
        value.startsWith('role:') &&
        PublicIdSchemas.role.safeParse(value.slice('role:'.length)).success,
      'Selecciona un rol válido.'
    )
])

/**
 * Guardarraíl para Mongoose, que no puede expresar esta unión con `enum`: `role:` lleva un id
 * dinámico. Mismo criterio que el schema, una sola vez, para que la base y el contrato no puedan
 * discrepar.
 */
export function isCreditStageNotification(value: unknown): boolean {
  return CreditStageNotificationSchema.safeParse(value).success
}

/**
 * CUÁNDO dispara el aviso de una etapa.
 *
 * Un efecto de ítem se dispara al marcarlo y no necesita momento: marcar ES el instante. Una etapa
 * dura semanas, así que su aviso tiene que decir de cuál de sus dos bordes habla — el de entrada
 * («arrancó el trámite») o el de salida («la etapa se cerró»). Sin el momento, «avisar en la etapa
 * 1» es una frase que el runtime no sabe ejecutar.
 */
export const STAGE_NOTICE_MOMENT = {
  /** Al ACTIVARSE la etapa. Es el camino del aviso interno de arranque. */
  ENTER: 'enter',
  /** Al CERRARSE la etapa, junto con su `exitedAt`. */
  EXIT: 'exit'
} as const
export type StageNoticeMoment = (typeof STAGE_NOTICE_MOMENT)[keyof typeof STAGE_NOTICE_MOMENT]
export const StageNoticeMomentSchema = z.enum([STAGE_NOTICE_MOMENT.ENTER, STAGE_NOTICE_MOMENT.EXIT])

/**
 * El aviso de una ETAPA: el efecto que no cuelga de ningún ítem.
 *
 * ☠️ Nace para reponer el aviso interno de arranque que la replicación del flujo canónico se llevó
 * puesto (SPEC-13): el aviso de inicio **pertenece al momento en que la etapa 1 abre**, no a un
 * ítem que alguien marca. Los destinatarios siguen siendo los de la etapa (`notification` /
 * `emailsNotification`) — acá sólo viven el momento, la etiqueta que va a la bitácora y al correo,
 * y el efecto con su identificador de plantilla.
 *
 * Desde SPEC-14 una etapa puede tener MÁS DE UNO (`stage.notices`) y cada uno lleva su audiencia y
 * su formato dentro de su `effect`: el arranque manda dos correos en el mismo borde —el interno al
 * equipo y el de marca al cliente— y son dos avisos, no uno con dos caras.
 */
export const CreditStageNoticeSchema = z.object({
  moment: StageNoticeMomentSchema,
  /** Lo que se lee en el correo y en la bitácora donde un ítem pondría su etiqueta. */
  label: z.string().trim().min(1).max(160),
  effect: CreditItemEffectSchema
})

export const CreditTemplateStageSchema = z.object({
  key: z.string().min(1),
  order: z.number().int().nonnegative(),
  name: z.string().min(1),
  description: z.string().nullable(),
  /** Catálogo compartido con los estados del workflow — el vocabulario ya existe, no se duplica. */
  notification: z.array(CreditStageNotificationSchema),
  emailsNotification: z.array(z.string().trim().email(MESSAGES.RECIPIENT_EMAIL)),
  /**
   * Días de servicio pactados para la etapa.
   *
   * Nace `null` en las 7 etapas y se publica igual: el negocio mide tiempos desde el día uno
   * (R7) pero todavía no acordó cuál es el bueno. Tener el campo desde el principio evita que
   * ponerle meta a una etapa sea una migración.
   */
  slaDays: z.number().int().positive().nullable(),
  /**
   * ⚠️ LEGACY: el aviso ÚNICO de la etapa, como lo escribió SPEC-10C.
   *
   * Se sigue leyendo —una versión publicada con él tiene que seguir avisando— pero ya no se
   * escribe: publicar llena `notices`. Ver el porqué del plural ahí abajo.
   */
  notice: CreditStageNoticeSchema.nullable().default(null),
  /**
   * Los avisos de la ETAPA, con su momento cada uno.
   *
   * ☠️ Era UNO solo y el flujo canónico pide DOS en el mismo borde: «Al iniciar: correo de aviso
   * al cliente y a los usuarios encargados (formatos DISTINTOS según cliente/usuario)». Con una
   * sola ranura, el segundo aviso pisaba al primero al publicar — y en silencio, porque el
   * compilador elegía uno.
   *
   * Cada aviso lleva su propia audiencia y su propio formato dentro de su `effect`, así que dos
   * avisos de la misma etapa ya no compiten por la lista de destinatarios de la etapa.
   */
  notices: z.array(CreditStageNoticeSchema).max(10).default([]),
  items: z.array(CreditTemplateItemSchema)
})

/**
 * UNA salida de la bifurcación: el valor que la decide y la etapa a la que manda.
 *
 * `value` es JOIN KEY —la clave de la opción del campo decisorio, jamás su etiqueta— y `target` es
 * la clave de una etapa. Es una LISTA de subdocumentos y no un objeto por modalidad porque el
 * dominio ya no es `{credit, cash}`: lo declara el campo que la condición apunta, y puede tener
 * tres valores o siete. Un objeto con claves dinámicas sería un `Map` de Mongoose, que vuelve como
 * `Map` desde un documento y como objeto plano desde un `lean()` — dos formas para el mismo dato.
 */
export const CreditBranchRouteSchema = z.object({
  value: z.string().trim().min(1).max(80),
  target: z.string().min(1)
})

/**
 * El CAMPO que decide la ruta, por sus tres claves estables.
 *
 * `null` = la ruta la decide la MODALIDAD del expediente (`credito.modalidad`), que no es un campo
 * del checklist sino un atributo propio del crédito. Es la forma que tenían todas las versiones
 * publicadas hasta SPEC-14, y sigue siendo legal.
 *
 * Se guardan las tres CLAVES y no el token `etapaN.<campo>`: el token lleva el `order` de la etapa,
 * que es un namespace de presentación y se corre al recablear el grafo. Las claves son JOIN KEYS y
 * no se mueven.
 */
export const CreditBranchDecidedBySchema = z.object({
  stageKey: z.string().min(1),
  itemKey: z.string().min(1),
  fieldKey: z.string().min(1)
})

/**
 * La bifurcación, DECLARADA y única.
 *
 * Se decide en una etapa nombrada y manda a N rutas nombradas. Es un dato de la plantilla y no un
 * `if` en el runtime, porque el editor de flujos (SPEC-10) tiene que poder dibujarla — y porque un
 * salto escondido en código es exactamente lo que hace que nadie sepa por qué una etapa quedó en
 * `skipped`.
 *
 * ── ☠️ LA MODALIDAD DEJÓ DE SER EL MECANISMO Y PASÓ A SER UN CASO ────────────
 *
 * Hasta SPEC-14 la bifurcación era literalmente «crédito o contado»: `routes` con esas dos claves
 * escritas en el contrato. El principio que fijó el lead es el contrario — «nada de la ruta queda
 * hardcodeado»: la condición evalúa CUALQUIER campo de dominio cerrado y cada opción tiene su
 * salida. Si mañana la ruta no es por modalidad, o aparece una tercera modalidad, no se toca
 * código.
 *
 * | campo | qué es |
 * |---|---|
 * | `variable` | el token que la condición evalúa (`credito.modalidad` o `etapaN.<campo>`) |
 * | `decidedBy` | el campo del checklist que lo responde, por claves. `null` = la modalidad |
 * | `routeList` | una salida por cada valor del dominio. **Cobertura total, sin default mágico** |
 * | `routes` | ⚠️ ESPEJO LEGADO de `routeList`, sólo cuando el dominio es exactamente `{credit, cash}` |
 *
 * `routes` se sigue escribiendo para que una plantilla de modalidad se lea igual desde cualquier
 * consumidor anterior, y es `null` en cuanto la ruta la decide otra cosa. **Nadie lo lee para
 * enrutar**: la única puerta de lectura es `normalizeKernelBranch`, que produce `routeList` desde
 * cualquiera de las dos formas.
 */
export const CreditBranchSchema = z.object({
  decidedAtStage: z.string().min(1),
  /**
   * Opcional porque una versión publicada antes de SPEC-10D no lo trae — y NULLABLE porque el doc
   * de Mongoose lo materializa en `null` por default: un borrador guardado vuelve con `variable:
   * null`, y un `.optional()` pelado (que acepta `undefined`, no `null`) rompía TODA lectura de la
   * plantilla con 500. Es el mismo par doc/contrato que `decidedBy` y `routes` ya declaran.
   */
  variable: z.string().trim().min(1).max(160).nullable().optional(),
  decidedBy: CreditBranchDecidedBySchema.nullable().default(null),
  routeList: z.array(CreditBranchRouteSchema).max(20).default([]),
  routes: z
    .object({
      credit: z.string().min(1),
      cash: z.string().min(1)
    })
    .nullable()
    .default(null)
})

/**
 * De dónde sale la decisión de ruta de un expediente, resuelta para la pantalla.
 *
 * `label` es lo que se le muestra a una persona —«Modalidad de pago», «Origen de los fondos»— y
 * `field` ubica el campo decisorio para que el checklist lo pueda distinguir. En la rama `financing`
 * no hay campo: la modalidad es un atributo del expediente, no un punto del checklist.
 */
export const CreditRouteDecisionSchema = z.object({
  kind: z.enum(['financing', 'field']),
  variable: z.string().min(1),
  label: z.string().min(1),
  /**
   * `true` cuando la etapa decisoria ya cerró. Corregir desde ahí requeriría rebobinar el proceso;
   * la edición ordinaria no lo hace y la pantalla debe presentar la decisión como sólo lectura.
   */
  locked: z.boolean(),
  field: z
    .object({
      stageKey: z.string().min(1),
      itemKey: z.string().min(1),
      fieldKey: z.string().min(1)
    })
    .nullable()
})

/**
 * Cómo NACE un expediente. Son dos caminos y conviven (R10).
 *
 * El automático se cuenta hacia atrás desde el último pago del acuerdo —«cuatro meses antes»— y el
 * offset es configurable porque el número salió de una conversación, no de una política escrita.
 *
 * ☠️ `onlyWhen.entityState` dice `'end'`, que es el TIPO de estado, no su nombre. Hoy el único
 * `type:'end'` es `PROMESA FIRMADA`, pero **se resuelve en runtime**: hardcodear el nombre deja el
 * disparo roto el día que alguien renombre el estado desde la pantalla de workflows.
 */
export const CreditTriggerSchema = z.object({
  auto: z.object({
    kind: z.literal('date_offset'),
    source: z.literal('lastPaymentDate'),
    /**
     * Cuánto se corre la fecha fuente. NEGATIVO es antes (la semilla declara −4 meses).
     *
     * `months` es el que existió siempre y sigue siendo obligatorio; `days` y `years` son una
     * extensión ADITIVA: una plantilla publicada como `{ months: -4 }` significa exactamente lo
     * mismo que antes, y las tres unidades se aplican encadenadas (año → mes → día) para que la
     * aritmética siga siendo de CALENDARIO — al 31 de enero menos un mes le toca el 28 de febrero,
     * y ninguna conversión a días da eso.
     */
    offset: z.object({
      months: z.number().int(),
      days: z.number().int().optional(),
      years: z.number().int().optional()
    }),
    onlyWhen: z.object({ entityState: z.literal('end') })
  }),
  manual: z.object({ permission: z.literal('StartCreditProcess') })
})

/**
 * Las perillas del proceso.
 *
 * ☠️ **`backlogCutoffDate` se fija a la fecha de salida a producción.** Sin ese corte, prender el
 * cron le abre expediente solo a todo lo que ya cumplía la condición: medido contra producción el
 * 2026-08-21, **26 negociaciones dispararían el día uno y 17 de ellas ya están atrasadas**. Nada
 * preexistente dispara solo.
 *
 * El corte se compara contra la fecha de INICIO del crédito (último pago + offset del disparo), no
 * contra el último pago: sólo arranca solo lo que tiene su inicio ese día o después; lo que ya debió
 * arrancar antes queda para el arranque manual.
 */
export const CreditKnobsSchema = z.object({
  autoStart: z.boolean(),
  autoNotify: z.boolean(),
  backlogCutoffDate: LocalDateSchema.nullable()
})

/** El estado de publicación de una versión. `draft` es la única editable. */
export const TEMPLATE_VERSION_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived'
} as const
export type TemplateVersionStatus =
  (typeof TEMPLATE_VERSION_STATUS)[keyof typeof TEMPLATE_VERSION_STATUS]
export const TemplateVersionStatusSchema = z.enum([
  TEMPLATE_VERSION_STATUS.DRAFT,
  TEMPLATE_VERSION_STATUS.PUBLISHED,
  TEMPLATE_VERSION_STATUS.ARCHIVED
])

/**
 * Los bloques que el editor representa en el lienzo.
 *
 * `item` es un nodo de PRIMERA CLASE desde SPEC-10C: cada ítem del checklist se dibuja colgando de
 * su etapa, y un aviso se ancla conectándole una arista (`Ítem → Aviso`) en vez de eligiéndolo en
 * un select. El anclaje es la ARISTA, que es lo que hace que se pueda encadenar.
 */
export const CREDIT_TEMPLATE_NODE_TYPE = {
  TRIGGER: 'trigger',
  STAGE: 'stage',
  ITEM: 'item',
  CONDITION: 'condition',
  ACTION: 'action',
  END: 'end'
} as const
export type CreditTemplateNodeType =
  (typeof CREDIT_TEMPLATE_NODE_TYPE)[keyof typeof CREDIT_TEMPLATE_NODE_TYPE]
export const CreditTemplateNodeTypeSchema = z.enum([
  CREDIT_TEMPLATE_NODE_TYPE.TRIGGER,
  CREDIT_TEMPLATE_NODE_TYPE.STAGE,
  CREDIT_TEMPLATE_NODE_TYPE.ITEM,
  CREDIT_TEMPLATE_NODE_TYPE.CONDITION,
  CREDIT_TEMPLATE_NODE_TYPE.ACTION,
  CREDIT_TEMPLATE_NODE_TYPE.END
])

/**
 * Cuántas salidas necesita una Condición para PUBLICARSE: una decisión con una sola rama no decide
 * nada.
 *
 * ☠️ Es una regla del publish, NO del contrato del borrador. Podar una opción del campo que alimenta
 * una Condición de dos salidas la deja con una, y el borrador tiene que poder guardarse igual: si el
 * esquema lo exigiera, «Guardar borrador» respondía 400 y el usuario perdía el trabajo sin entender
 * por qué. El compilador la hace cumplir con `CONDITION_OUTPUT_REQUIRED` y el editor la avisa en el
 * nodo y en su panel.
 */
export const CREDIT_CONDITION_MIN_OUTPUTS = 2

/** Una salida visible de una Condición. `value` es estable; `label` se puede traducir. */
export const CreditTemplateConditionOutputSchema = z.object({
  value: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(120)
})

/**
 * El Aviso es AGNÓSTICO: manda un correo y nada más.
 *
 * ☠️ Antes convivía con «Avisar a Postventa», y era una confusión de capas: enviar un correo no
 * sabe de Postventa. A quién llega lo deciden los DESTINATARIOS, y que un aviso además emita el
 * evento de Postventa lo decide su interruptor `signal` (NT1-4a) — nunca fue un tipo de acción
 * distinto. El valor legacy se traduce al leer un grafo viejo (`hydrateCreditTemplateGraph`) y no
 * vuelve a escribirse.
 */
export const CREDIT_TEMPLATE_ACTION = {
  SEND_EMAIL: 'send_email'
} as const
export type CreditTemplateAction =
  (typeof CREDIT_TEMPLATE_ACTION)[keyof typeof CREDIT_TEMPLATE_ACTION]

/**
 * Lo que un Aviso dispara ADEMÁS de su correo. Hoy hay una sola: el pase a Postventa.
 *
 * ☠️ Es un dato EXPLÍCITO del nodo desde NT1-4a. Antes el pase colgaba del identificador de
 * plantilla (`credit-approved`), que el panel no mostraba ni validaba: recrear el nodo o cambiarle
 * el identificador apagaba Postventa en silencio. El runtime lee la misma marca en el efecto
 * compilado (`effect.signal`).
 */
export const CREDIT_NOTICE_SIGNAL = {
  /** El pase a Postventa: correo de aprobación + evento `CREDIT_APPROVED`. */
  CREDIT_APPROVED: 'credit_approved'
} as const
export type CreditNoticeSignal = (typeof CREDIT_NOTICE_SIGNAL)[keyof typeof CREDIT_NOTICE_SIGNAL]

/**
 * Configuración editable de los nodos que sí aportan semántica al compilador.
 *
 * Etapa, disparador y fin no necesitan un blob paralelo: sus datos viven en el contrato estable.
 * Condición y Acción sí son nodos de primera clase; guardar su configuración acá evita volver a
 * derivarlos de `branch`/`effect` y hace que el grafo sea la fuente de verdad.
 */
export const CreditTemplateGraphNodeConfigSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal(CREDIT_TEMPLATE_NODE_TYPE.CONDITION),
    variable: z.string().trim().min(1).max(160),
    /** Sin mínimo a propósito: ver `CREDIT_CONDITION_MIN_OUTPUTS`, que se exige al publicar. */
    outputs: z.array(CreditTemplateConditionOutputSchema).max(20)
  }),
  z.object({
    kind: z.literal(CREDIT_TEMPLATE_NODE_TYPE.ACTION),
    action: z.enum([CREDIT_TEMPLATE_ACTION.SEND_EMAIL]).default(CREDIT_TEMPLATE_ACTION.SEND_EMAIL),
    /**
     * CUÁNDO dispara, y sólo se lee cuando el Aviso cuelga de una ETAPA.
     *
     * Anclado a un ítem el momento no existe: marcar ES el instante. Anclado a una etapa hay dos
     * bordes posibles y hay que elegir uno, así que el campo vive en el nodo —que es donde el
     * panel lo pregunta— y viaja a `stage.notice.moment` al publicar.
     */
    moment: StageNoticeMomentSchema.default(STAGE_NOTICE_MOMENT.EXIT),
    subject: z.string().trim().min(1).max(240),
    previewText: z.string().trim().max(320),
    /**
     * ☠️ IDENTIFICADOR de la plantilla que ejecuta el runtime (`credit-approved`,
     * `credit-stage-notice`, …), NO el cuerpo del correo.
     *
     * Viaja al `effect.template` del ítem al publicar y es lo que el handler mira para decidir
     * qué manda. Escribir el cuerpo acá dejaba el aviso sin identificador y llenaba la bitácora
     * con párrafos: el cuerpo vive en `body`.
     */
    template: z.string().trim().min(1).max(120),
    /**
     * El interruptor «Este aviso pasa la negociación a Postventa».
     *
     * Tres estados, y los tres significan algo distinto:
     *
     * | Valor | Significa |
     * |---|---|
     * | `'credit_approved'` | el aviso es el pase a Postventa |
     * | `null` | alguien lo APAGÓ: el aviso sólo manda su correo |
     * | ausente | un nodo anterior al interruptor: la marca se deriva del identificador legado al hidratar |
     *
     * ⚠️ Opcional y SIN default a propósito: un default convertiría todos los nodos guardados antes
     * del interruptor en «apagado», y el aviso de aprobación publicado dejaría de pasar a Postventa.
     */
    signal: z.enum([CREDIT_NOTICE_SIGNAL.CREDIT_APPROVED]).nullable().optional(),
    /**
     * El contenido redactado en el editor, con tokens `{{ … }}` de variables río arriba.
     *
     * Se valida al publicar —ninguna variable puede ser de aguas abajo— y **viaja al correo**:
     * publicar lo copia al `message` del efecto, que es lo que el adaptador renderiza. Hasta
     * SPEC-14 se guardaba acá y no llegaba a ningún lado.
     */
    body: z.string().max(20_000).default(''),
    /**
     * Lo mismo que `body`, pero como AST del editor de bloques: chips de variable, fórmulas y
     * condicionales en vez de tokens `{{ … }}` dentro de una cadena.
     *
     * Es lo que el editor escribe desde SPEC-15. `body` se conserva y se sigue guardando —el texto
     * llano del AST— para que una versión publicada hoy siga siendo legible por cualquier consumidor
     * anterior, y para que el diff de una plantilla no sea un JSON opaco.
     */
    content: CreditEmailContentSchema.nullable().default(null),
    /** `recipients` se conserva sólo para abrir borradores anteriores: todo migra a `to`. */
    recipients: CreditRecipientListSchema.optional(),
    to: CreditRecipientListSchema.optional(),
    cc: CreditRecipientListSchema.optional(),
    bcc: CreditRecipientListSchema.optional(),
    replyTo: CreditRecipientListSchema.max(1).optional()
  })
])

/**
 * Proyección explícita del grafo para el editor.
 *
 * `stageKey`/`itemKey` enlazan el nodo visual con la definición materializable; no se derivan de
 * la etiqueta. Los nodos sintéticos (disparador, condición y fin) dejan ambas referencias en
 * `null`.
 */
export const CreditTemplateGraphNodeSchema = z.object({
  id: z.string().trim().min(1).max(120),
  type: CreditTemplateNodeTypeSchema,
  label: z.string().trim().min(1).max(160),
  position: z.object({ x: z.number().finite(), y: z.number().finite() }),
  stageKey: z.string().trim().min(1).nullable(),
  itemKey: z.string().trim().min(1).nullable(),
  /**
   * El nodo que CONTIENE a este, cuando la contención es la que lo dibuja.
   *
   * Una Etapa es un GRUPO estilo n8n y sus Puntos viven adentro: `parentId` es el nodo de la etapa
   * y la `position` del hijo pasa a ser RELATIVA a ese grupo, que es como lo modela xyflow. Sólo lo
   * llevan los Puntos; todo lo demás lo deja en `null`.
   *
   * ⚠️ El TAMAÑO del grupo no se persiste: se calcula siempre desde la cantidad de puntos. Un ancho
   * guardado envejece mal —agregar un punto lo dejaría corto sin que nadie lo note— y el contrato
   * no gana nada llevándolo.
   */
  parentId: z.string().trim().min(1).max(120).nullable().default(null),
  /** `null` mantiene compatibles las versiones de SPEC-10; el derive las hidrata al leer. */
  config: CreditTemplateGraphNodeConfigSchema.nullable().default(null)
})

export const CreditTemplateGraphEdgeSchema = z.object({
  id: z.string().trim().min(1).max(160),
  source: z.string().trim().min(1).max(120),
  target: z.string().trim().min(1).max(120),
  sourceHandle: z.string().trim().min(1).max(80).nullable(),
  targetHandle: z.string().trim().min(1).max(80).nullable(),
  label: z.string().trim().max(160).nullable()
})

export const CreditTemplateGraphSchema = z.object({
  nodes: z.array(CreditTemplateGraphNodeSchema).min(1).max(250),
  edges: z.array(CreditTemplateGraphEdgeSchema).max(500)
})

export const CreditTemplateVersionSchema = z.object({
  id: PublicIdSchemas.creditTemplateVersion,
  template: PublicIdSchemas.creditTemplate,
  version: z.number().int().positive(),
  status: TemplateVersionStatusSchema,
  stages: z.array(CreditTemplateStageSchema),
  branch: CreditBranchSchema,
  trigger: CreditTriggerSchema,
  knobs: CreditKnobsSchema,
  graph: CreditTemplateGraphSchema
})

export const CreditTemplateSchema = z.object({
  id: PublicIdSchemas.creditTemplate,
  name: z.string().min(1),
  description: z.string().nullable(),
  /** La versión editable, si hay una abierta. */
  draftVersion: PublicIdSchemas.creditTemplateVersion.nullable(),
  /** La que se le materializa a todo expediente nuevo. `null` = la plantilla no arrancó nunca. */
  activeVersion: PublicIdSchemas.creditTemplateVersion.nullable(),
  active: z.boolean(),
  createdBy: CreditUserRefSchema,
  updatedBy: CreditUserRefSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

export const CreditTemplateVariableSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  category: z.string().min(1),
  /**
   * El vocabulario del catálogo, tal como lo declara `CREDIT_BINDING_VARIABLE_METADATA`.
   *
   * ⭐ `url` entró con `expediente.enlace`: es el destino del botón «Ver expediente», y ahora que
   * ese botón es CONTENIDO su enlace tiene que llegar al editor como una variable más. El tipo no
   * es decorativo — `emailDocHref` resuelve el token y después exige `https:`—, así que declararlo
   * `string` habría escondido que ese valor tiene una forma que se hace cumplir.
   */
  type: z.enum(['string', 'number', 'boolean', 'date', 'email', 'file', 'url']),
  example: z.union([z.string(), z.number(), z.boolean()]).optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  insertable: z.boolean().optional()
})

/** La plantilla estable, su versión activa y el borrador editable cuando existe. */
export const V2CurrentCreditTemplateSchema = z.object({
  template: CreditTemplateSchema,
  /** `null` en una copia recién creada: nace con borrador y todavía no fue publicada. */
  activeVersion: CreditTemplateVersionSchema.nullable(),
  draftVersion: CreditTemplateVersionSchema.nullable(),
  availableVariables: z.record(z.array(CreditTemplateVariableSchema))
})

/** Guardado completo del borrador. No valida publicabilidad; publicar es otro comando. */
export const V2CreditTemplateDraftInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2_000).nullable(),
  version: z.object({
    stages: z.array(CreditTemplateStageSchema),
    branch: CreditBranchSchema,
    trigger: CreditTriggerSchema,
    knobs: CreditKnobsSchema,
    graph: CreditTemplateGraphSchema
  })
})

/** Identidad pública de una plantilla administrable. Nunca viaja el ObjectId de Mongo. */
export const V2CreditTemplateParamsSchema = z.object({
  templateId: PublicIdSchemas.creditTemplate
})

/** Aviso del grafo que se renderiza y envía al usuario autenticado. */
export const V2CreditTemplateTestEmailInputSchema = z.object({
  noticeId: z.string().trim().min(1).max(120)
})

/** Evidencia mínima del envío: el destinatario lo decide el servidor, jamás el request. */
export const V2CreditTemplateTestEmailResultSchema = z.object({
  sentTo: z.string().email(),
  subject: z.string().min(1)
})

/** Resultado público de una imagen del editor almacenada en R2/SeaweedFS. */
export const V2CreditTemplateAssetSchema = z.object({
  url: z
    .string()
    .url()
    .refine((value) => value.startsWith('https://'), 'La URL pública debe usar HTTPS.'),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  contentType: z.enum(['image/png', 'image/jpeg', 'image/gif', 'image/webp']),
  size: z
    .number()
    .int()
    .positive()
    .max(5 * 1024 * 1024)
})

/** Metadata liviana de una versión; el grafo completo queda fuera de este listado. */
export const V2CreditTemplateVersionSummarySchema = z.object({
  id: PublicIdSchemas.creditTemplateVersion,
  version: z.number().int().positive(),
  status: TemplateVersionStatusSchema,
  isActive: z.boolean(),
  createdAt: InstantSchema,
  createdBy: CreditUserRefSchema,
  /** `null` mientras siga siendo borrador. En publicadas queda sellado por el publish. */
  publishedAt: InstantSchema.nullable(),
  publishedBy: CreditUserRefSchema
})

export const V2CreditTemplateVersionsSchema = z.object({
  data: z.array(V2CreditTemplateVersionSummarySchema)
})

export const CREDIT_TEMPLATE_DELETE_BLOCK_REASON = {
  SYSTEM_TEMPLATE: 'system_template',
  IN_USE: 'in_use'
} as const
export type CreditTemplateDeleteBlockReason =
  (typeof CREDIT_TEMPLATE_DELETE_BLOCK_REASON)[keyof typeof CREDIT_TEMPLATE_DELETE_BLOCK_REASON]

/** Metadata de chrome: suficiente para detalles y para decidir si «Eliminar» es viable. */
export const V2CreditTemplateDetailsSchema = z.object({
  template: CreditTemplateSchema,
  activeVersionNumber: z.number().int().positive().nullable(),
  draftVersionNumber: z.number().int().positive().nullable(),
  versionCount: z.number().int().nonnegative(),
  canDelete: z.boolean(),
  deleteBlockedReason: z
    .enum([
      CREDIT_TEMPLATE_DELETE_BLOCK_REASON.SYSTEM_TEMPLATE,
      CREDIT_TEMPLATE_DELETE_BLOCK_REASON.IN_USE
    ])
    .nullable()
})

export const V2DeleteCreditTemplateResultSchema = z.object({
  id: PublicIdSchemas.creditTemplate,
  deleted: z.literal(true)
})

/* ────────────────────────────────────────────────────────────────────────────
 * EL EXPEDIENTE: `crd_`
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Un campo del expediente: la declaración de la plantilla + lo que se respondió.
 *
 * `answeredAt`/`answeredBy` van acá y no dentro de `value` porque sobreviven a que alguien borre
 * la respuesta: «se contestó y se deshizo» y «nunca se contestó» son estados distintos, y el
 * reporte de tiempos por evento (R7) necesita distinguirlos.
 */
export const CreditFieldSchema = CreditTemplateFieldSchema.extend({
  value: CreditFieldValueSchema.nullable(),
  answeredAt: InstantSchema.nullable(),
  answeredBy: CreditUserRefSchema
})

/** Un ítem del expediente: el sello de quién lo marcó y cuándo (R1). */
export const CreditItemSchema = z.object({
  key: z.string().min(1),
  order: z.number().int().nonnegative(),
  label: z.string().min(1),
  hint: z.string().nullable(),
  appliesTo: ItemAppliesToSchema,
  checked: z.boolean(),
  checkedAt: InstantSchema.nullable(),
  checkedBy: CreditUserRefSchema,
  note: z.string().nullable(),
  fields: z.array(CreditFieldSchema)
})

/**
 * Una etapa del expediente.
 *
 * `enteredAt`/`exitedAt` son campos propios y no se derivan de la bitácora: son la materia prima
 * del «cuánto tarda cada etapa» (R7), y una consulta agregada sobre un historial no se indexa.
 */
export const CreditStageSchema = z.object({
  key: z.string().min(1),
  order: z.number().int().nonnegative(),
  name: z.string().min(1),
  status: StageStatusSchema,
  enteredAt: InstantSchema.nullable(),
  exitedAt: InstantSchema.nullable(),
  completedBy: CreditUserRefSchema,
  items: z.array(CreditItemSchema)
})

/**
 * Una entrada de la bitácora. Forma de `HistoryWorkflow`
 * (`apps/api-real-estate/src/share/models/history-workflows.schema.ts`), que el módulo comparte en
 * vez de inventar la suya: el historial de una negociación y el de su crédito se leen juntos.
 */
export const CreditHistoryEntrySchema = z.object({
  stageKey: z.string().min(1),
  startDate: InstantSchema.nullable(),
  endDate: InstantSchema.nullable(),
  updatedBy: CreditUserRefSchema,
  comment: z.string().nullable(),
  skipped: z.boolean()
})

/* ────────────────────────────────────────────────────────────────────────────
 * EL LISTADO
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Estado de la FILA del listado.
 *
 * Tiene un valor más que el expediente, y es el más numeroso: **`not_started`** —la negociación
 * cerró y el trámite no arrancó—. Son 137 de 163 el día uno (medido 2026-08-21), así que el
 * listado se arma desde NEGOCIACIONES y no desde `credits`: si arrancara de los expedientes, la
 * mayoría de las filas no existiría y con ellas se iría el único camino para empezar.
 *
 * Es exactamente la lección que ya pagó Postventa (`V2AdminHandoverStatus`).
 */
export const CREDIT_ROW_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved',
  CANCELLED: 'cancelled'
} as const
export type CreditRowStatus = (typeof CREDIT_ROW_STATUS)[keyof typeof CREDIT_ROW_STATUS]
export const CreditRowStatusSchema = z.enum([
  CREDIT_ROW_STATUS.NOT_STARTED,
  CREDIT_ROW_STATUS.IN_PROGRESS,
  CREDIT_ROW_STATUS.APPROVED,
  CREDIT_ROW_STATUS.CANCELLED
])

/** El expediente visto DESDE la fila: lo mínimo para saber en qué anda. `null` = no arrancó. */
export const CreditRowRefSchema = z
  .object({
    id: PublicIdSchemas.credit,
    code: z.number().int(),
    startedAt: InstantSchema,
    /** Nació del cron, no de una persona. Distingue el backlog del trabajo deliberado. */
    autoStarted: z.boolean(),
    approvedAt: InstantSchema.nullable()
  })
  .nullable()

/**
 * La FILA: una negociación cerrada y el estado de su trámite.
 *
 * Se direcciona por `neg_` —no por `crd_`— porque la fila ES la negociación: hay a lo sumo un
 * expediente vivo por negociación, y el `neg_` la identifica igual cuando el expediente todavía no
 * existe. Mismo criterio que el listado de Postventa.
 */
export const V2AdminCreditRowSchema = z.object({
  id: PublicIdSchemas.negotiation,
  /** El contador de la negociación —«la 168»—, no un código de catálogo. */
  code: z.number().int(),
  status: CreditRowStatusSchema,
  credit: CreditRowRefSchema,
  /** En qué etapa está parado. `null` mientras no arrancó. */
  currentStageKey: z.string().nullable(),
  /** Días parado en la etapa actual: la columna que dice dónde se está atorando el trámite. */
  daysInStage: z.number().int().nonnegative().nullable(),
  financingType: FinancingTypeSchema.nullable(),
  /** La última cuota del acuerdo de pago. Es el ancla del disparo automático. */
  lastPaymentDate: LocalDateSchema.nullable(),
  /** Cuándo debería arrancar según el offset de la plantilla (`lastPaymentDate` − 4 meses). */
  startsAt: LocalDateSchema.nullable(),
  property: namedRef(PublicIdSchemas.property),
  project: namedRef(PublicIdSchemas.project),
  advisor: CreditUserRefSchema,
  /**
   * Los propietarios. Array porque una negociación admite varios; llega ordenado con el principal
   * primero —quién manda entre varios es regla del dominio, no decisión de la tabla—.
   */
  clients: z.array(
    z.object({
      id: PublicIdSchemas.client,
      name: z.string(),
      code: z.number().int().nullable(),
      /** Sólo la persona individual tiene foto; la jurídica cae a iniciales. */
      photo: z.string().nullable(),
      isDefault: z.boolean()
    })
  )
})

/** Listado cursor-paginado (ADR 0019). Los totales viven en `/summary`. */
export const V2AdminCreditsListSchema = z.object({
  data: z.array(V2AdminCreditRowSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Totales del listado, con los mismos filtros.
 *
 * Se cuentan aparte y no sumando las filas: con cursor no hay página completa que sumar.
 */
export const V2AdminCreditsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: z.object({
    notStarted: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative()
  }),
  /**
   * «Próximos»: los que todavía no arrancaron Y tienen fecha de disparo conocida.
   *
   * Va afuera de `byStatus` porque NO es un estado: es un subconjunto de `notStarted` —el que tiene
   * plan de pagos, y por lo tanto un `startsAt` que ordenar—. Meterlo adentro haría que las cuatro
   * ramas dejaran de sumar `total`, que es la propiedad por la que se leen.
   */
  upcoming: z.number().int().nonnegative()
})

/* ────────────────────────────────────────────────────────────────────────────
 * EL DETALLE
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * El expediente completo, direccionado por `crd_`.
 *
 * `templateVersion` viaja como referencia y no como el contenido de la plantilla: las etapas de
 * ACÁ ya son la copia materializada, así que mandar también el original invitaría a que la
 * pantalla leyera el equivocado.
 */
export const V2AdminCreditDetailSchema = z.object({
  id: PublicIdSchemas.credit,
  code: z.number().int(),
  status: CreditStatusSchema,
  negotiation: z.object({
    id: PublicIdSchemas.negotiation,
    code: z.number().int(),
    property: namedRef(PublicIdSchemas.property),
    project: namedRef(PublicIdSchemas.project),
    advisor: CreditUserRefSchema
  }),
  templateVersion: z.object({
    id: PublicIdSchemas.creditTemplateVersion,
    template: PublicIdSchemas.creditTemplate,
    version: z.number().int().positive()
  }),
  financingType: FinancingTypeSchema.nullable(),
  currentStageKey: z.string().nullable(),
  startedAt: InstantSchema,
  autoStarted: z.boolean(),
  approvedAt: InstantSchema.nullable(),
  /** La bifurcación materializada: la pantalla la necesita para saber qué etapa sigue a cuál. */
  branch: CreditBranchSchema,
  /**
   * QUÉ decide la ruta de ESTE expediente, resuelto para la pantalla.
   *
   * ☠️ Sin esto el frontend tendría que volver a razonar la bifurcación: leer `branch.decidedBy`,
   * buscar el campo en las etapas y decidir si el selector del pie corresponde. Es la clase de
   * lógica que termina divergiendo del servidor — y el síntoma sería una pantalla ofreciendo elegir
   * la modalidad en una plantilla que no enruta por modalidad.
   *
   * | `kind` | qué significa |
   * |---|---|
   * | `financing` | la decide la MODALIDAD: el selector del pie se muestra y se mantiene sincronizado |
   * | `field` | la decide un campo del checklist: el selector se oculta y el campo lleva su distintivo |
   *
   * `null` = plantilla lineal, sin bifurcación.
   */
  routeDecision: CreditRouteDecisionSchema.nullable().default(null),
  stages: z.array(CreditStageSchema),
  /**
   * ⛔ LOS HECHOS DEL EXPEDIENTE — con qué se decide si un campo se ve.
   *
   * Un campo con `showWhen` se muestra o no según lo que el expediente diga HOY. La pantalla no
   * puede derivarlo sola: tendría que volver a razonar qué token nombra a qué campo, con qué
   * prefijo y cómo se traduce cada valor a texto — cuatro decisiones que el servidor ya tomó y que
   * duplicadas se separan en la primera corrección. El síntoma sería el peor: la pantalla esconde
   * un campo y el backend lo exige, y la etapa no cierra por algo que nadie ve.
   *
   * Es EL MISMO mapa (`stageVisibilityFacts`) con el que el kernel exime del requisito a un campo
   * oculto. No es un espejo: es el mismo builder.
   *
   * ── LEER LA AUSENCIA Y LEER EL `null` NO ES LO MISMO ─────────────────────────
   *
   * | | qué significa | qué hace la condición |
   * |---|---|---|
   * | la clave está con valor | se respondió | se compara |
   * | la clave está en `null` | la variable existe y nadie la respondió | se compara contra vacío |
   * | la clave NO está | el proceso no produce esa variable | condición ilegible → el campo SE VE |
   *
   * ⚠️ Lleva las variables que producen las ETAPAS y nada más. Las del binding —`cliente.nombre`,
   * `credito.modalidad`— quedan afuera a propósito: pedirlas obligaría a cargar la negociación y su
   * acuerdo de pago en cada lectura, y sobre todo dejarían de poder ser el mismo mapa en las dos
   * capas. Una condición que las referencia cae en el fail-open —el campo se ve, el requisito se
   * exige—, que es el lado seguro. Para «según la modalidad» está `appliesTo` en el punto.
   */
  variableFacts: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]).nullable())
    .default({}),
  history: z.array(CreditHistoryEntrySchema),
  createdBy: CreditUserRefSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/* ────────────────────────────────────────────────────────────────────────────
 * ENTRADAS
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Abrir el expediente a mano — el camino manual de R10.
 *
 * `financingType` es opcional a propósito: 129 de 163 negociaciones cerradas no la traen declarada
 * (79%, medido 2026-08-21), así que exigirla acá frenaría el alta del caso más común. Se elige
 * después, en la primera etapa, que es donde el proceso la pide.
 */
export const V2StartCreditInputSchema = z.object({
  negotiation: PublicIdSchemas.negotiation.describe(MESSAGES.NEGOTIATION_REQUIRED),
  financingType: FinancingTypeSchema.optional()
})

/**
 * Marcar o desmarcar un ítem. El ítem se direcciona por la URL (`stageKey`/`itemKey`).
 *
 * Acepta `false` porque desmarcar es parte del trabajo real: alguien tilda de más y lo corrige.
 * Cada transición sella fecha y usuario (R1) y queda en bitácora.
 */
export const V2CheckCreditItemInputSchema = z.object({
  checked: z.boolean(),
  note: z.string().trim().max(2000, MESSAGES.NOTE_MAX).optional()
})

/**
 * Responder un campo.
 *
 * `null` es un valor legítimo y significa «borrá lo que había», no «no cambies nada»: sin él, un
 * dato cargado por error sería imposible de sacar.
 */
export const V2AnswerCreditFieldInputSchema = z.object({
  value: CreditFieldValueSchema.nullable()
})

/**
 * Cerrar la etapa y abrir la siguiente.
 *
 * ☠️ `acknowledgeWarnings` es el corazón de R3 —«no tiene que bloquear, pero sí avisarnos»—. La
 * API contesta con la lista de lo que falta; el segundo intento la trae en `true` y pasa igual.
 * Los `blocking` NO se saltean con esto: para eso están, y son la excepción declarada.
 */
export const V2AdvanceCreditStageInputSchema = z.object({
  acknowledgeWarnings: z.boolean()
})

/**
 * Elegir o CORREGIR la modalidad de pago.
 *
 * Va por su propio endpoint y no escondida en un PATCH genérico porque no es editar un campo: es
 * la bifurcación del proceso. Cambiarla mueve etapas entre `skipped` y `pending`.
 *
 * Se puede corregir mientras el crédito no esté aprobado, y toda corrección va a bitácora — por
 * eso el `reason`.
 */
export const V2SetCreditFinancingTypeInputSchema = z.object({
  financingType: FinancingTypeSchema.describe(MESSAGES.FINANCING_TYPE_REQUIRED),
  reason: z.string().trim().max(500, MESSAGES.REASON_MAX).optional()
})

/* ────────────────────────────────────────────────────────────────────────────
 * SALIDAS DE ESCRITURA
 *
 * Las escrituras devuelven el EXPEDIENTE RECIÉN LEÍDO (`V2AdminCreditDetailSchema`) y no una
 * confirmación propia: la pantalla ya sabe pintar esa forma, y una segunda representación del mismo
 * expediente son dos presentadores que se separan. Marcar un ítem, responder un campo, elegir la
 * modalidad y abrir el trámite se contestan así, sin schema nuevo.
 *
 * La ÚNICA que necesita forma propia es avanzar de etapa, y no por comodidad: lo que devuelve no se
 * puede derivar del expediente.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Lo que se le advierte a quien avanza. **Nada de esto frena** (R3).
 *
 * `uncheckedItems` son los ítems aplicables que quedaron sin marcar; `emptyRequiredFields`, los
 * campos `required` vacíos de ítems YA marcados. Los dos llevan sus etiquetas además de sus claves
 * para que la pantalla arme el mensaje sin volver a recorrer la plantilla.
 */
export const CreditAdvanceWarningsSchema = z.object({
  uncheckedItems: z.array(z.object({ key: z.string(), label: z.string() })),
  emptyRequiredFields: z.array(
    z.object({
      itemKey: z.string(),
      itemLabel: z.string(),
      fieldKey: z.string(),
      fieldLabel: z.string()
    })
  )
})

/**
 * El resultado de cerrar una etapa.
 *
 * ── POR QUÉ ESTA SÍ NECESITA SCHEMA PROPIO ───────────────────────────────────
 *
 * Porque `needs_acknowledgement` **no escribió nada**: el expediente vuelve idéntico a como estaba,
 * así que mirarlo no dice que faltaba algo ni QUÉ faltaba. Esa lista la calcula la política del
 * kernel al intentar el avance y no existe en ningún campo persistido — sin este envoltorio, el
 * corazón de R3 («no tiene que bloquear, pero sí avisarnos») no tendría por dónde viajar.
 *
 * Discriminado por el STRING `status`, como todo lo que se ramifica en esta app:
 *
 * | `status` | qué pasó |
 * |---|---|
 * | `needs_acknowledgement` | Hay advertencias y no se confirmaron. **Nada se escribió.** El segundo intento manda `acknowledgeWarnings: true` |
 * | `advanced` | La etapa se cerró y se abrió la siguiente |
 * | `completed` | Era la última del recorrido: el expediente queda aprobado |
 *
 * `credit` viaja en los tres casos —también en el primero, donde es el expediente sin tocar— para
 * que la pantalla nunca tenga que decidir si le llegó o no el recurso.
 *
 * ⛔ Los campos `blocking` vacíos NO salen por acá: son 409 `CREDIT_STAGE_BLOCKED` con su lista en
 * `details`. Son la excepción declarada, no una advertencia.
 */
export const V2AdvanceCreditStageResultSchema = z.object({
  status: z.enum(['needs_acknowledgement', 'advanced', 'completed']),
  warnings: CreditAdvanceWarningsSchema,
  credit: V2AdminCreditDetailSchema
})

/* ────────────────────────────────────────────────────────────────────────────
 * TIPOS
 * ──────────────────────────────────────────────────────────────────────────── */

export type CreditRecipient = z.infer<typeof CreditRecipientSchema>
export type CreditUserRef = z.infer<typeof CreditUserRefSchema>
export type CreditAttachmentRef = z.infer<typeof CreditAttachmentRefSchema>
export type CreditFieldConfig = z.infer<typeof CreditFieldConfigSchema>
export type CreditFieldValue = z.infer<typeof CreditFieldValueSchema>
export type CreditTemplateField = z.infer<typeof CreditTemplateFieldSchema>
export type CreditItemEffect = z.infer<typeof CreditItemEffectSchema>
export type CreditNoticeMessage = z.infer<typeof CreditNoticeMessageSchema>
export type CreditEffectRule = z.infer<typeof CreditEffectRuleSchema>
export type CreditTemplateItem = z.infer<typeof CreditTemplateItemSchema>
export type CreditTemplateStage = z.infer<typeof CreditTemplateStageSchema>
export type CreditStageNotification = z.infer<typeof CreditStageNotificationSchema>
export type CreditBranch = z.infer<typeof CreditBranchSchema>
export type CreditBranchRoute = z.infer<typeof CreditBranchRouteSchema>
export type CreditBranchDecidedBy = z.infer<typeof CreditBranchDecidedBySchema>
export type CreditRouteDecision = z.infer<typeof CreditRouteDecisionSchema>
export type CreditTrigger = z.infer<typeof CreditTriggerSchema>
export type CreditKnobs = z.infer<typeof CreditKnobsSchema>
export type CreditStageNotice = z.infer<typeof CreditStageNoticeSchema>
export type CreditTemplateGraphNodeConfig = z.infer<typeof CreditTemplateGraphNodeConfigSchema>
export type CreditTemplateGraphNode = z.infer<typeof CreditTemplateGraphNodeSchema>
export type CreditTemplateGraphEdge = z.infer<typeof CreditTemplateGraphEdgeSchema>
export type CreditTemplateGraph = z.infer<typeof CreditTemplateGraphSchema>
export type CreditTemplateVersion = z.infer<typeof CreditTemplateVersionSchema>
export type CreditTemplate = z.infer<typeof CreditTemplateSchema>
export type CreditTemplateVariable = z.infer<typeof CreditTemplateVariableSchema>
export type V2CurrentCreditTemplate = z.infer<typeof V2CurrentCreditTemplateSchema>
export type V2CreditTemplateDraftInput = z.infer<typeof V2CreditTemplateDraftInputSchema>
export type V2CreditTemplateParams = z.infer<typeof V2CreditTemplateParamsSchema>
export type V2CreditTemplateTestEmailInput = z.infer<typeof V2CreditTemplateTestEmailInputSchema>
export type V2CreditTemplateTestEmailResult = z.infer<typeof V2CreditTemplateTestEmailResultSchema>
export type V2CreditTemplateAsset = z.infer<typeof V2CreditTemplateAssetSchema>
export type V2CreditTemplateVersionSummary = z.infer<typeof V2CreditTemplateVersionSummarySchema>
export type V2CreditTemplateVersions = z.infer<typeof V2CreditTemplateVersionsSchema>
export type V2CreditTemplateDetails = z.infer<typeof V2CreditTemplateDetailsSchema>
export type V2DeleteCreditTemplateResult = z.infer<typeof V2DeleteCreditTemplateResultSchema>
export type CreditField = z.infer<typeof CreditFieldSchema>
export type CreditItem = z.infer<typeof CreditItemSchema>
export type CreditStage = z.infer<typeof CreditStageSchema>
export type CreditHistoryEntry = z.infer<typeof CreditHistoryEntrySchema>
export type CreditRowRef = z.infer<typeof CreditRowRefSchema>
export type V2AdminCreditRow = z.infer<typeof V2AdminCreditRowSchema>
export type V2AdminCreditsList = z.infer<typeof V2AdminCreditsListSchema>
export type V2AdminCreditsSummary = z.infer<typeof V2AdminCreditsSummarySchema>
export type V2AdminCreditDetail = z.infer<typeof V2AdminCreditDetailSchema>
export type V2StartCreditInput = z.infer<typeof V2StartCreditInputSchema>
export type V2CheckCreditItemInput = z.infer<typeof V2CheckCreditItemInputSchema>
export type V2AnswerCreditFieldInput = z.infer<typeof V2AnswerCreditFieldInputSchema>
export type V2AdvanceCreditStageInput = z.infer<typeof V2AdvanceCreditStageInputSchema>
export type V2SetCreditFinancingTypeInput = z.infer<typeof V2SetCreditFinancingTypeInputSchema>
export type CreditAdvanceWarnings = z.infer<typeof CreditAdvanceWarningsSchema>
export type V2AdvanceCreditStageResult = z.infer<typeof V2AdvanceCreditStageResultSchema>
